/**
 * @module EventService.EventBlocks
 */
function createEventBlocks(logService, util, _window) {
    const _eventBlocks = {};
    const groups = {};

    function log(params) {
        params.module = 'eventBlocks';
        logService.log(params);
        return params;
    }

    /**
     * @param {EventBlock} a A block to compare
     * @param {EventBlock} b Another block to comapre
     * @returns {Boolean} True if the given block overlap by eventIds or time range
     */
    function overlap(a, b) {
        if (a.firstId === b.lastId
                || a.lastId === b.firstId
                || a.firstId === b.firstId
                || a.lastId === b.lastId) {
            return true;
        }

        // can't use equality with dates since multiple events can have the exact same date
        if ((a.firstDateValue < b.firstDateValue && a.lastDateValue > b.firstDateValue) ||
                (a.firstDateValue < b.lastDateValue && a.lastDateValue > b.lastDateValue)) {
            return true;
        }

        return false;
    }

    /**
     * @param {EventBlock} a A block to compare
     * @param {EventBlock} b Another block to comapre
     * @returns {EventBlock} Between a and b, returns the block that is oldest,
     *     assuming a and b are EventBlocks, otherwise undefined
     */
    function oldest(a, b) {
        if (a.firstDateValue && b.firstDateValue) {
            if (b.firstDateValue < a.firstDateValue) {
                return b;
            }
            return a;
        }
    }

    /**
     * @param {EventBlock} a A block to compare
     * @param {EventBlock} b Another block to comapre
     * @returns {EventBlock} Between a and b, returns the block that is newest,
     *     assuming a and b are EventBlocks, otherwise undefined
     */
    function newest(a, b) {
        if (a.lastDateValue && b.lastDateValue) {
            if (b.lastDateValue > a.lastDateValue) {
                return b;
            }
            return a;
        }
    }

    /**
     * @param {String} blockGroupId
     * @returns {BlockGroup} returns and creates if necessary a BlockGroup for the given blockGroupId
     */
    function getGroup(blockGroupId) {
        if (blockGroupId) {
            groups[blockGroupId] ??= {
                blocks: [],
                id: blockGroupId,
                hasEarliestEvent: false,
                hasLatestEvent: false
            };
            return groups[blockGroupId];
        }
    }

    /**
     * @param {String} blockGroupId ID of the BlockGroup to check
     * @returns {Boolean} true if the BlockGroup has the earliest event
     */
    _eventBlocks.hasEarliestEvent = function(blockGroupId) {
        const group = getGroup(blockGroupId);
        if (group) {
            return (group.hasEarliestEvent);
        }
        return false;
    };

    /**
     * @param {String} blockGroupId ID of the BlockGroup to check
     * @returns {Boolean} true if the BlockGroup has the latest event
     */
    _eventBlocks.hasLatestEvent = function(blockGroupId) {
        const group = getGroup(blockGroupId);
        if (group) {
            return (group.hasLatestEvent);
        }
        return false;
    };

    /* eslint-disable complexity */
    /**
     * @description Creates an event block from events get API results, and
     *     combining with existing blocks when possible.
     * @param {Object} eventsResult The results from an events get API request
     * @param {Object} eventsResult.params Parameters used when making the request,
     *     and includes extra values not used as query string params
     * @param {Array} eventsResult.events An array of event results
     */
    _eventBlocks.add = function(eventsResult) {
        // get the block group to add to
        let group;
        let params = eventsResult?.params;
        if (params?.srcType !== 'user') {
            if (params) {
                if (params.eventType) {
                    if (params.srcId) {
                        group = getGroup(params.eventType + params.srcId);
                    } else {
                        group = getGroup(params.eventType);
                    }
                } else if (params.srcId) {
                    group = getGroup(params.srcId);
                } else if (params.action === 'id' && eventsResult?.events[0]) {
                    group = getGroup(eventsResult?.events[0].srcId);
                } else {
                    group = getGroup('all');
                }
            } else {
                group = getGroup('all');
            }
        } else {
            group = getGroup('user');
        }

        // new block
        let newBlock;
        if (eventsResult.events.length > 0) {

            const lastEvent = eventsResult.events[eventsResult.events.length -1];

            // baseline values are from the first and last events in the result
            newBlock = {
                firstId: eventsResult.events[0].id,
                firstDateValue: eventsResult.events[0].fullDateValue,
                lastId: lastEvent.id,
                lastDateValue: lastEvent.fullDateValue,
                sinceEvent: params?.sinceEvent,
                beforeEvent: params?.beforeEvent
            };

            // use request parameters to improve date values and increase a beneficial chance of overlap
            if (params.action === 'startDttm') {
                newBlock.firstDateValue = new _window.Date(params.startDttm).getTime();
            }
            if (params.action === 'endDttm') {
                newBlock.lastDateValue = new _window.Date(params.endDttm).getTime();
            }
            if (params.action === 'pre' && params.date
                    && newBlock.lastDateValue < params.date) {
                newBlock.lastDateValue = eventsResult.params.date;
            }
            if (params.action === 'fol' && params.date
                    && newBlock.firstDateValue > params.date) {
                newBlock.firstDateValue = eventsResult.params.date;
            }
            if (!params.action) {
                group.hasLatestEvent = true;
            }
        }

        // group boundaries
        if (eventsResult.events.length === 0 ||
                (params.limit && eventsResult.params.limit > eventsResult.events.length)) {
            if (params.action === 'pre') {
                group.hasEarliestEvent = true;
            } else if (params.action === 'fol') {
                group.hasLatestEvent = true;
            } else if (params.action === 'startDttm') {
                group.hasLatestEvent = true;
            } else if (params.action === 'endDttm') {
                group.hasEarliestEvent = true;
            } else if (!params.action) {
                group.hasEarliestEvent = true;
                group.hasLatestEvent = true;
            }
        }
        if (!params.action) {
            group.hasLatestEvent = true;
        }

        // combine blocks that are contiguous by API call info
        if (newBlock) {
            group.blocks.every(function(item) {
                if ((newBlock.sinceEvent && item.lastId === newBlock.sinceEvent)
                        || (newBlock.beforeEvent && item.firstId === newBlock.beforeEvent)) {
                    // combine blocks
                    const oldestItem = oldest(item, newBlock);
                    const newestItem = newest(item, newBlock);
                    item.firstId = oldestItem.firstId;
                    item.firstDateValue = oldestItem.firstDateValue;
                    item.lastId = newestItem.lastId;
                    item.lastDateValue = newestItem.lastDateValue;
                    newBlock = undefined;
                    return false;
                }

                if (newBlock.firstDateValue === item.lastDateValue) {
                    item.lastDateValue = newBlock.lastDateValue;
                    item.lastId = newBlock.lastId;
                    newBlock = undefined;
                    return false;
                }

                if (newBlock.lastDateValue === item.firstDateValue) {
                    item.firstDateValue = newBlock.firstDateValue;
                    item.firstId = newBlock.firstId;
                    newBlock = undefined;
                    return false;
                }

                return true;
            });
        }

        if (!params) {
            log({
                code: 'missing params',
                object: eventsResult
            });
        }

        if (newBlock) {
            // do not use "delete" to keep the object shapes consistent for optimization
            newBlock.sinceEvent = undefined;
            newBlock.beforeEvent = undefined;
            group.blocks.push(newBlock);
        }

        // combine blocks that are contiguous by overlapping
        let combined = false;
        let i = 0;
        let itemI;
        while (i < group.blocks.length - 1) {
            itemI = group.blocks[i];
            for (let j = i + 1; j < group.blocks.length; j += 1) {
                if (overlap(itemI, group.blocks[j])) {
                    // combine blocks
                    const oldestItem = oldest(group.blocks[j], itemI);
                    const newestItem = newest(group.blocks[j], itemI);
                    itemI.firstId = oldestItem.firstId;
                    itemI.firstDateValue = oldestItem.firstDateValue;
                    itemI.lastId = newestItem.lastId;
                    itemI.lastDateValue = newestItem.lastDateValue;
                    group.blocks.splice(j, 1);
                    combined = true;
                    break;
                }
            }

            if (combined) {
                i = 0;
                combined = false;
            } else {
                i += 1;
            }
        }
    };
    /* eslint-enable complexity */

    /**
     * @param {String} blockGroupId
     * @param {Number} firstDateValue start of the range in Unix epoch milliseconds
     * @param {Number} lastDateValue end of the range in Unix epoch milliseconds
     * @returns {EventBlock} A single block that covers the entire given time range if it exists, otherwise undefined
     */
    _eventBlocks.blockForContiguousDateRange = function(blockGroupId, firstDateValue, lastDateValue) {
        if (blockGroupId) {
            const group = getGroup(blockGroupId);
            const latest = _eventBlocks.latestBlockForGroup(blockGroupId);
            const earliest = _eventBlocks.earliestBlockForGroup(blockGroupId);
            const block = group.blocks.find(item =>
                (item.firstDateValue < firstDateValue && lastDateValue < item.lastDateValue)
                        || (item.firstDateValue < firstDateValue && item === latest && group.hasLatestEvent)
                        || (lastDateValue < item.lastDateValue && item === earliest && group.hasEarliestEvent)
            );
            if (block) {
                return util.merge({}, block);
            }
        }
    };

    /**
     * @param {String} blockGroupId
     * @param {Event} firstEvent first Event in the range
     * @param {Event} lastEvent last Event in the range
     * @returns {EventBlock} A single block that covers the entire range of events between
     *     firstEvent and lastEvent, if it exists, otherwise undeifned
     */
    _eventBlocks.blockForContiguousEventRange = function(blockGroupId, firstEvent, lastEvent) {
        const group = getGroup(blockGroupId);
        if (group) {
            const block = group.blocks.find(item =>
                (item.firstDateValue < firstEvent.fullDateValue || item.firstId === firstEvent.id)
                    && (lastEvent.fullDateValue < item.lastDateValue || item.lastId === lastEvent.id)
            );
            if (block) {
                return util.merge({}, block);
            }
        }
    };

    /**
     * @param {String} blockGroupId
     * @param {Event} event An Event to find a EventBlock for
     * @returns {EventBlock} An EventBlock that contains the given event if it exists, otherwise undefined
     */
    _eventBlocks.blockForEvent = function(blockGroupId, event) {
        const group = getGroup(blockGroupId);
        if (event && group) {
            const block = group.blocks.find(item =>
                (item.firstDateValue < event.fullDateValue && item.lastDateValue > event.fullDateValue)
                        || item.firstId === event.id
                        || item.lastId === event.id
            );
            if (block) {
                return util.merge({}, block);
            }
        }
    };

    /**
     * @description Because clients can only work with clones of EventBlocks,
     *     they can't compare by reference to check for equivalence.
     *     This function allows clients to compare EventBlocks by value to check for equivalence.
     * @param {EventBlock} block1 An EventBlock to comapare
     * @param {EventBlock} block2 Another EventBlock to comapare
     * @returns {Boolean} true if the given blocks are equivalent,
     *     even if they are not the same object by reference.
     *     Otherwise false. True if both blocks are undefined or null.
     */
    _eventBlocks.equals = function(block1, block2) {
        return (block1
            && block2
            && block1.firstId === block2.firstId
            && block1.firstDateValue === block2.firstDateValue
            && block1.lastId === block2.lastId
            && block1.lastDateValue === block2.lastDateValue)
            || (!block1 && !block2);
    };

    /**
     * @param {String} blockGroupId
     * @returns {Number} The number of blocks in the group if the group exists, otherwise undefined
     */
    _eventBlocks.blockCount = function(blockGroupId) {
        const group = getGroup(blockGroupId);
        if (group) {
            return group.blocks.length;
        }
    };

    /**
     * @param {String} blockGroupId
     * @returns {EventBlock} The earliest block in the group for the given blockGroupId
     */
    _eventBlocks.earliestBlockForGroup = function(blockGroupId) {
        const group = getGroup(blockGroupId);
        if (group) {
            if (group.blocks.length === 0) {
                return;
            }

            if (group.blocks.length === 1) {
                return group.blocks[0];
            }

            let block = group.blocks[0];

            group.blocks.forEach(function(item) {
                if (item.firstDateValue < block.firstDateValue) {
                    block = item;
                }
            });

            return block;
        }
    };

    /**
     * @param {String} blockGroupId
     * @returns {EventBlock} The latest block in the group for the given blockGroupId
     */
    _eventBlocks.latestBlockForGroup = function(blockGroupId) {
        const group = getGroup(blockGroupId);
        if (group) {
            if (group.blocks.length === 0) {
                return;
            }

            if (group.blocks.length === 1) {
                return group.blocks[0];
            }

            let block = group.blocks[0];

            group.blocks.forEach(function(item) {
                if (item.lastDateValue > block.lastDateValue) {
                    block = item;
                }
            });

            return block;
        }
    };

    /**
     * @description Gets a block for a single point in time
     * @param {String} blockGroupId Represents various ways events can be retrieved.
     *     Could be a deviceId when getting events by device, or sourceType such as 'favorites', 'user' or 'all'.
     * @param {Number} time the time to get an eventBlock for in Unix epoch milliseconds
     */
    _eventBlocks.blockForTime = function(blockGroupId, time) {
        const group = getGroup(blockGroupId);
        if (group) {
            // most obviously, find a block that includes that requested time
            let block = group.blocks.find(item => (item.firstDateValue < time) && (item.lastDateValue > time));

            // if the blockGroup has the latest event and the time is later, return the latest block
            if (!block) {
                const latest = _eventBlocks.latestBlockForGroup(blockGroupId);
                if (latest && latest.lastDateValue <= time && group.hasLatestEvent) {
                    block = latest;
                }
            }

            // if the blockGroup has the earliest event and the time is earlier, return the earliest block
            if (!block) {
                const earliest = _eventBlocks.earliestBlockForGroup(blockGroupId);
                if (earliest && earliest.firstDateValue >= time && group.hasEarliestEvent) {
                    block = earliest;
                }
            }

            // return a clone of the block so that clients can't modify or access internal data
            if (block) {
                return util.merge({}, block);
            }
        }
    };

    _eventBlocks.clear = function() {
        util.clear(groups);
    };

    return Object.freeze(_eventBlocks);
}

export default createEventBlocks;
